/**
 * The cells of a table card that its write did not reach, named for the card's
 * reason line: a cell the page does not hold is never written back (it stands in
 * its row, and text written back would land in a neighbour's cell), so the card
 * reads partly applied and names it. One reading for both writers of a table card:
 * Apply All's fold (`partlyAppliedCardsOf`) and the applier a single Apply and a
 * one-card Apply All write through (`ProposalApplier.applyOnBookmarks`).
 *
 * Each cell is named by its row as the card listed it: the first cell of the run of
 * missing cells it sits in, then the cell itself, so a row the user deleted reads
 * "Acceptance testing / Week 6" and not "Week 6" alone.
 */
import { paragraphIsWrittenWhereGone } from "@common/utils/redline/redline-source";
import { TextEditPlanner } from "@common/utils/redline/text-edit-planner";
import type { RedlineTextParagraph } from "@common/utils/redline/wire/redline-payload";

import { cellsNotWrittenReasonOf } from "./errors";

/** Where a missing cell is: gone from the document, or still there but not returned by Word. */
export interface CardCellPresence {
    /** True when the page the card was written over holds the cell's bookmark. */
    cellIsOnPage: (bookmarkName: string) => boolean;
    /** True when Word no longer finds the cell's bookmark: the user removed its row or cell. */
    cellIsGone: (bookmarkName: string) => boolean;
}

/** The labels of a table card's cells that were not written, by why. */
export interface CardCellsNotWritten {
    goneCellLabels: string[];
    unreturnedCellLabels: string[];
}

/**
 * The card's changed cells its write did not reach, each labelled by its row.
 *
 * CASES, per card paragraph the page does not hold
 *   - the card leaves it as it is                         -> not named: nothing of the card is missing
 *   - it is gone, and the card empties it                 -> not named: the row is already gone
 *   - it is gone                                          -> named as gone
 *   - Word still holds it but did not return it           -> named as not returned
 *   - it carries no bookmark                              -> not named: it names no cell
 *
 * EXAMPLE, the card lists rows 2 and 3 of a schedule, row 3 deleted by the user
 *   card paragraphs  ["Integration", "Supplier", "Week 4" -> "Week 5", "Acceptance testing", "Supplier", "Week 6" -> "Week 7"]
 *   missing          the last three, gone
 *   -> gone ["Acceptance testing / Week 6"]: the run of gone cells starts at "Acceptance testing"
 *   the same, Word did not return row 3 (still in the document)
 *   -> not returned ["Week 6"]
 */
export function cardCellsNotWrittenOf(cardParagraphs: RedlineTextParagraph[], presence: CardCellPresence): CardCellsNotWritten {
    const goneCellLabels: string[] = [];
    const unreturnedCellLabels: string[] = [];
    /** The first cell of the run of gone cells being walked (the row the user deleted); null outside such a run. */
    let goneRunFirstText: string | null = null;
    for (const cardParagraph of cardParagraphs) {
        const cellBookmark = cardParagraph.bookmark;
        /** WHEN the card paragraph names no cell CONSEQUENCE it neither starts nor ends a run of gone cells. */
        if (cellBookmark === null) continue;
        const cellIsGone = !presence.cellIsOnPage(cellBookmark) && presence.cellIsGone(cellBookmark);
        /**
         * CASES
         *   - the cell is gone       -> it opens a run of gone cells, or carries on the one it is in
         *   - it is on the page, or
         *     Word did not return it -> any run of gone cells ends here
         */
        goneRunFirstText = cellIsGone ? goneRunFirstText ?? cardParagraph.textWhenProposed : null;
        /** WHEN the cell is on the page, or the card leaves it as it is CONSEQUENCE nothing of the card is missing there. */
        if (presence.cellIsOnPage(cellBookmark) || TextEditPlanner.requestWritesNothing(cardParagraph.textWhenProposed, cardParagraph.textProposed)) continue;
        /** WHEN Word still holds the cell and did not return it CONSEQUENCE named by its own text: its row is in the document. */
        if (goneRunFirstText === null) {
            unreturnedCellLabels.push(cardParagraph.textWhenProposed.trim());
            continue;
        }
        /** WHEN the card empties a gone cell (`paragraphIsWrittenWhereGone` false) CONSEQUENCE not named: the row is already gone. */
        if (!paragraphIsWrittenWhereGone(cardParagraph.textWhenProposed, cardParagraph.textProposed)) continue;
        /**
         * CASES
         *   - the cell opens its run of gone cells -> its own text
         *   - it follows another                   -> the run's first cell, then its own text
         *
         * EDGE CASE
         *   Two rows deleted together are one run: a cell of the second is named after the first
         *   row's first cell. The card lists cells, not rows, and the rows are gone.
         */
        goneCellLabels.push(goneRunFirstText === cardParagraph.textWhenProposed ? goneRunFirstText.trim() : `${goneRunFirstText.trim()} / ${cardParagraph.textWhenProposed.trim()}`);
    }
    return { goneCellLabels, unreturnedCellLabels };
}

/**
 * The sentence naming the card's cells its write did not reach (`cardCellsNotWrittenOf`).
 *
 * NULL
 *   every changed cell of the card was written.
 */
export function cardCellsNotWrittenReasonOrNullOf(cardParagraphs: RedlineTextParagraph[], presence: CardCellPresence): string | null {
    const { goneCellLabels, unreturnedCellLabels } = cardCellsNotWrittenOf(cardParagraphs, presence);
    return goneCellLabels.length + unreturnedCellLabels.length === 0 ? null : cellsNotWrittenReasonOf(goneCellLabels, unreturnedCellLabels);
}
